import React, { useEffect, useMemo, useState } from 'react';
import { Pencil, Plus, Search, Trash2, X } from 'lucide-react';

import Topbar from '../components/Topbar';
import { useAuth } from '../context/AuthContext';
import {
  createDoctor,
  deleteDoctor,
  listDoctors,
  updateDoctor,
  type DoctorCreateInput,
} from '../services/adminService';
import { ApiError } from '../services/api';
import type { AuthUser } from '../services/authService';
import './Dashboard.css';
import './AdminDashboard.css';

type DoctorFormState = DoctorCreateInput;

const emptyDoctorForm: DoctorFormState = {
  email: '',
  password: '',
  full_name: '',
  is_active: true,
};

const AdminDashboard = () => {
  const { accessToken, logout } = useAuth();
  const [doctors, setDoctors] = useState<AuthUser[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const [editingDoctor, setEditingDoctor] = useState<AuthUser | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [form, setForm] = useState<DoctorFormState>(emptyDoctorForm);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadDoctors = async () => {
    if (!accessToken) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await listDoctors(accessToken);
      setDoctors(response.doctors);
    } catch (requestError) {
      if (requestError instanceof ApiError && requestError.status === 401) {
        logout();
        return;
      }
      setError(
        requestError instanceof Error ? requestError.message : 'Doctors could not be loaded.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void loadDoctors();
  }, [accessToken]);

  const filteredDoctors = useMemo(() => {
    const query = searchValue.trim().toLowerCase();
    return doctors.filter(doctor =>
      [doctor.full_name, doctor.email, doctor.is_active ? 'active' : 'inactive']
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(query)
    );
  }, [doctors, searchValue]);

  const openCreateForm = () => {
    setEditingDoctor(null);
    setForm(emptyDoctorForm);
    setError(null);
    setIsFormOpen(true);
  };

  const openEditForm = (doctor: AuthUser) => {
    setEditingDoctor(doctor);
    setForm({
      email: doctor.email,
      password: '',
      full_name: doctor.full_name,
      is_active: doctor.is_active,
    });
    setError(null);
    setIsFormOpen(true);
  };

  const closeForm = () => {
    if (isSaving) return;
    setIsFormOpen(false);
    setEditingDoctor(null);
    setForm(emptyDoctorForm);
    setError(null);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!accessToken || isSaving) return;

    setIsSaving(true);
    setError(null);
    try {
      if (editingDoctor) {
        const updated = await updateDoctor(
          editingDoctor.id,
          {
            email: form.email,
            full_name: form.full_name || null,
            is_active: form.is_active,
            ...(form.password ? { password: form.password } : {}),
          },
          accessToken
        );
        setDoctors(current => current.map(doctor => (doctor.id === updated.id ? updated : doctor)));
      } else {
        const created = await createDoctor(
          { ...form, full_name: form.full_name || null },
          accessToken
        );
        setDoctors(current => [created, ...current]);
      }
      setIsFormOpen(false);
      setEditingDoctor(null);
      setForm(emptyDoctorForm);
      setError(null);
    } catch (requestError) {
      if (requestError instanceof ApiError && requestError.status === 401) {
        logout();
        return;
      }
      setError(requestError instanceof Error ? requestError.message : 'Doctor could not be saved.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (doctor: AuthUser) => {
    if (!accessToken || !window.confirm(`Delete ${doctor.full_name || doctor.email}?`)) return;
    setError(null);
    try {
      await deleteDoctor(doctor.id, accessToken);
      setDoctors(current => current.filter(item => item.id !== doctor.id));
    } catch (requestError) {
      if (requestError instanceof ApiError && requestError.status === 401) {
        logout();
        return;
      }
      setError(
        requestError instanceof Error ? requestError.message : 'Doctor could not be deleted.'
      );
    }
  };

  return (
    <div className="dashboard-container">
      <main className="main-content">
        <div className="content-wrapper">
          <Topbar title="Administration" />

          <section className="table-section full-width-table-section">
            <div className="table-header admin-table-header">
              <div>
                <h2 className="table-title">Doctors</h2>
                <p className="table-subtitle">{doctors.length} doctor accounts</p>
              </div>

              <div className="admin-actions">
                <div className="search-container table-search-container">
                  <Search
                    className="search-icon"
                    aria-hidden="true"
                  />
                  <input
                    className="search-input"
                    type="search"
                    value={searchValue}
                    onChange={event => setSearchValue(event.target.value)}
                    placeholder="Search doctors"
                  />
                </div>
                <button
                  className="admin-primary-button"
                  type="button"
                  onClick={openCreateForm}
                >
                  <Plus size={17} />
                  Add doctor
                </button>
              </div>
            </div>

            {error && !isFormOpen && <div className="admin-error">{error}</div>}

            <div className="table-container">
              <table className="data-table admin-doctor-table">
                <thead className="data-table-head">
                  <tr>
                    <th className="data-table-th">Doctor</th>
                    <th className="data-table-th">Email</th>
                    <th className="data-table-th">Status</th>
                    <th className="data-table-th data-table-th-actions">Actions</th>
                  </tr>
                </thead>
                <tbody className="data-table-body">
                  {!isLoading &&
                    filteredDoctors.map(doctor => (
                      <tr
                        className="data-table-tr"
                        key={doctor.id}
                      >
                        <td className="data-table-td admin-doctor-name">
                          {doctor.full_name || 'Unnamed doctor'}
                        </td>
                        <td className="data-table-td">{doctor.email}</td>
                        <td className="data-table-td">
                          <span
                            className={`admin-status ${doctor.is_active ? 'active' : 'inactive'}`}
                          >
                            {doctor.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="data-table-td data-table-td-actions">
                          <div className="admin-row-actions">
                            <button
                              type="button"
                              onClick={() => openEditForm(doctor)}
                              title="Edit doctor"
                            >
                              <Pencil size={16} />
                            </button>
                            <button
                              type="button"
                              className="danger"
                              onClick={() => void handleDelete(doctor)}
                              title="Delete doctor"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  {!isLoading && filteredDoctors.length === 0 && (
                    <tr>
                      <td
                        className="empty-table-cell"
                        colSpan={4}
                      >
                        No doctors found.
                      </td>
                    </tr>
                  )}
                  {isLoading && (
                    <tr>
                      <td
                        className="empty-table-cell"
                        colSpan={4}
                      >
                        Loading doctors...
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </main>

      {isFormOpen && (
        <div
          className="admin-dialog-backdrop"
          role="presentation"
        >
          <div
            className="admin-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="doctor-form-title"
          >
            <div className="admin-dialog-header">
              <h2 id="doctor-form-title">{editingDoctor ? 'Edit doctor' : 'Add doctor'}</h2>
              <button
                type="button"
                onClick={closeForm}
                title="Close"
              >
                <X size={18} />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <label>
                Full name
                <input
                  value={form.full_name || ''}
                  onChange={event =>
                    setForm(current => ({ ...current, full_name: event.target.value }))
                  }
                  maxLength={255}
                />
              </label>
              <label>
                Email
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={event =>
                    setForm(current => ({ ...current, email: event.target.value }))
                  }
                />
              </label>
              <label>
                {editingDoctor ? 'New password' : 'Password'}
                <input
                  type="password"
                  required={!editingDoctor}
                  minLength={8}
                  value={form.password}
                  onChange={event =>
                    setForm(current => ({ ...current, password: event.target.value }))
                  }
                  placeholder={editingDoctor ? 'Leave empty to keep current' : ''}
                />
              </label>
              <label className="admin-active-toggle">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={event =>
                    setForm(current => ({ ...current, is_active: event.target.checked }))
                  }
                />
                Active account
              </label>
              {error && <p className="admin-form-error">{error}</p>}
              <div className="admin-dialog-footer">
                <button
                  type="button"
                  className="admin-secondary-button"
                  onClick={closeForm}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="admin-primary-button"
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
